import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const Header = ({ connectionStatus }) => {
  const location = useLocation();
  const { logout } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);
  
  const isActive = (path) => location.pathname === path;
  
  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-green-600';
      case 'disconnected': return 'text-red-600';
      default: return 'text-yellow-600';
    }
  };
  
  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return 'Connected';
      case 'disconnected': return 'Offline';
      default: return 'Checking...';
    }
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Logout failed');
    }
    setLoggingOut(false);
  };

  return (
    <header className="bg-white shadow-sm border-b">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center space-x-2">
            <span className="text-2xl">🏔️</span>
            <h1 className="text-xl font-bold text-gray-900">
              Hiking Safety Alert
            </h1>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/') 
                  ? 'bg-green-100 text-green-700' 
                  : 'text-gray-600 hover:text-green-700'
              }`}
            >
              Dashboard
            </Link>
            <Link
              to="/create"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/create') 
                  ? 'bg-green-100 text-green-700' 
                  : 'text-gray-600 hover:text-green-700'
              }`}
            >
              New Journey
            </Link>
            <Link
              to="/journeys"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/journeys') 
                  ? 'bg-green-100 text-green-700' 
                  : 'text-gray-600 hover:text-green-700'
              }`}
            >
              All Journeys
            </Link>
            <Link
              to="/settings"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/settings') 
                  ? 'bg-green-100 text-green-700' 
                  : 'text-gray-600 hover:text-green-700'
              }`}
            >
              Settings
            </Link>
          </nav>

          {/* Status indicator and logout */}
          <div className="flex items-center space-x-4">
            {/* Logout Button */}
            <button
              onClick={handleLogout}
              disabled={loggingOut}
              className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-red-600 transition-colors disabled:opacity-50 flex items-center space-x-1"
            >
              {loggingOut ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Logging out...</span>  
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Logout</span>
                </>
              )}
            </button>
            
            {/* Status */}
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                connectionStatus === 'connected' ? 'bg-green-500' :
                connectionStatus === 'disconnected' ? 'bg-red-500' : 'bg-yellow-500'
              }`}></div>
              <span className={`text-sm font-medium ${getStatusColor()}`}>
                {getStatusText()}
              </span>
            </div>
          </div>
        </div>

        {/* Mobile navigation */}
        <div className="md:hidden pb-3">
          <div className="flex flex-wrap items-center justify-between">
            <div className="flex space-x-1">
              <Link
                to="/"
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  isActive('/') 
                    ? 'bg-green-100 text-green-700' 
                    : 'text-gray-600'
                }`}
              >
                Home
              </Link>
              <Link
                to="/create"
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  isActive('/create') 
                    ? 'bg-green-100 text-green-700' 
                    : 'text-gray-600'
                }`}
              >
                Create
              </Link>
              <Link
                to="/journeys"
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  isActive('/journeys') 
                    ? 'bg-green-100 text-green-700' 
                    : 'text-gray-600'
                }`}
              >
                List
              </Link>
              <Link
                to="/settings"
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  isActive('/settings') 
                    ? 'bg-green-100 text-green-700' 
                    : 'text-gray-600'
                }`}
              >
                Settings
              </Link>
            </div>
            
            {/* Mobile Logout */}
            <button
              onClick={handleLogout}
              disabled={loggingOut}
              className="px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
            >
              {loggingOut ? 'Logging out...' : 'Logout'}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;